import moment, { Moment } from "moment-jalaali";
import React, { ChangeEventHandler, useContext, useState } from "react";
import { useForm } from "react-hook-form";

import CustomerApi from "../../../api/customer";
import UploadApi from "../../../api/upload";
import AuthContext from "../../../components/auth-context";
import useAppNavigate from "../../../components/router/use-app-navigate";

import DatePicker from "../../../components/inputs/date-picker";
import Filebox from "../../../components/inputs/filebox";
import Form from "../../../components/inputs/form";
import Select from "../../../components/inputs/select";
import Submit from "../../../components/inputs/submit";
import Textbox from "../../../components/inputs/textbox";

interface RequestForm {
  isMale: string;
  firstName: string;
  lastName: string;
  fatherName: string;
  nationalCode: string;
}

export interface IChangeIdentityProps {}

export default function ChangeIdentity(props: IChangeIdentityProps) {
  const context = useContext(AuthContext);
  const customer = context.customerInfo;

  const navigate = useAppNavigate();

  const requiredMessage = "این فیلد الزامی است";

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RequestForm>({
    mode: "onSubmit",
    reValidateMode: "onChange",
    values: {
      isMale:
        customer?.isMale === undefined || customer?.isMale === null
          ? ""
          : String(customer.isMale),
      firstName: customer?.firstName ? String(customer.firstName) : "",
      lastName: customer?.lastName ? String(customer.lastName) : "",
      fatherName: customer?.fatherName ? String(customer.fatherName) : "",
      nationalCode: customer?.nationalCode ? String(customer.nationalCode) : "",
    },
  });

  const [fileId, setFileId] = useState<number | undefined>(
    customer?.nationalCardImageId
  );
  const [isSubmit, setIsSubmit] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [fileError, setFileError] = useState("");
  const [birthDateError, setBirthDateError] = useState("");

  const [birthDate, setBirthDate] = useState<Moment | undefined>(
    customer?.birthDate
      ? moment(String(customer.birthDate), "jYYYYjMMjDD")
      : undefined
  );

  const onSubmit = async (data: RequestForm): Promise<string> => {
    setErrorMessage("");
    setFileError("");
    setBirthDateError("");

    let hasCustomError = false;

    if (!fileId) {
      setFileError("تصویر کارت ملی الزامی است");
      hasCustomError = true;
    }

    if (!birthDate || !birthDate.isValid()) {
      setBirthDateError("وارد کردن تاریخ تولد الزامی است");
      hasCustomError = true;
    } else {
      const maxAllowedBirthDate = moment().subtract(18, "year").startOf("day");

      if (birthDate.isAfter(maxAllowedBirthDate)) {
        setBirthDateError("سن باید حداقل ۱۸ سال باشد");
        hasCustomError = true;
      }

      const minAllowedBirthDate = moment().subtract(120, "year").startOf("day");

      if (birthDate.isBefore(minAllowedBirthDate)) {
        setBirthDateError("تاریخ تولد معتبر نیست");
        hasCustomError = true;
      }
    }

    if (hasCustomError) {
      return "لطفاً خطاهای فرم را برطرف کنید";
    }

    setIsSubmit(true);

    try {
      const response = await new CustomerApi(context).put({
        isMale: data.isMale === "true",
        firstName: String(data.firstName ?? "").trim(),
        lastName: String(data.lastName ?? "").trim(),
        fatherName: String(data.fatherName ?? "").trim(),
        nationalCode: String(data.nationalCode ?? "").trim(),
        birthDate: Number(birthDate!.format("jYYYYjMMjDD")),
        nationalCardImageId: fileId!,
      });

      if (response.errorMessage) {
        setErrorMessage(response.errorMessage);
        return response.errorMessage;
      }

      navigate("../");
      return "";
    } catch {
      const message = "خطایی هنگام ارسال اطلاعات رخ داد";
      setErrorMessage(message);
      return message;
    } finally {
      setIsSubmit(false);
    }
  };

  const onFileChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    setFileError("");

    const file = event.target.files?.[0];

    if (!file) {
      setFileId(undefined);
      return;
    }

    setIsSubmit(true);

    new UploadApi(context)
      .post(file)
      .then((uploadedFileId) => {
        setFileId(uploadedFileId);
      })
      .catch(() => {
        setFileId(undefined);
        setFileError("آپلود تصویر کارت ملی با خطا مواجه شد");
      })
      .finally(() => {
        setIsSubmit(false);
      });
  };

  if (!customer) {
    return null;
  }

  return (
    <Form submit={onSubmit} handleSubmit={handleSubmit}>
      {errorMessage && (
        <div className="text-red-500 text-right p-3 mb-4 bg-red-50 rounded border border-red-200 text-sm">
          {errorMessage}
        </div>
      )}

      <Filebox name="file" label="تصویر کارت ملی" onChange={onFileChange} />

      {fileError && <p className="text-red-500 text-sm mt-1">{fileError}</p>}

      <div>
        <Select
          {...register("isMale", {
            required: "انتخاب جنسیت الزامی است",
            validate: (value) =>
              String(value ?? "").trim().length > 0 ||
              "انتخاب جنسیت الزامی است",
          })}
          label="جنسیت"
          options={[
            {
              name: "انتخاب کنید...",
              value: "",
            },
            {
              name: "خانم",
              value: "false",
            },
            {
              name: "آقای",
              value: "true",
            },
          ]}
        />
        {errors.isMale?.message && (
          <p className="text-red-500 text-sm mt-1">{errors.isMale.message}</p>
        )}
      </div>

      <Textbox
        {...register("firstName", {
          required: requiredMessage,
          validate: (value) =>
            String(value ?? "").trim().length > 0 || requiredMessage,
        })}
        label="نام"
        errorMessage={errors.firstName?.message}
      />

      <Textbox
        {...register("lastName", {
          required: requiredMessage,
          validate: (value) =>
            String(value ?? "").trim().length > 0 || requiredMessage,
        })}
        label="نام خانوادگی"
        errorMessage={errors.lastName?.message}
      />

      <Textbox
        {...register("fatherName", {
          required: requiredMessage,
          validate: (value) =>
            String(value ?? "").trim().length > 0 || requiredMessage,
        })}
        label="نام پدر"
        errorMessage={errors.fatherName?.message}
      />

      <div>
        <DatePicker
          max={moment().add(-18, "year")}
          min={moment().add(-120, "year")}
          label="تاریح تولد"
          value={birthDate}
          onChange={setBirthDate}
        />

        {birthDateError && (
          <p className="text-red-500 text-sm mt-1">{birthDateError}</p>
        )}
      </div>

      <Textbox
        {...register("nationalCode", {
          required: "کد ملی الزامی است",
          validate: (value) =>
            String(value ?? "").trim().length > 0 || "کد ملی الزامی است",
          pattern: {
            value: /^[0-9۰-۹]+$/,
            message: "کد ملی فقط باید شامل عدد باشد",
          },
          minLength: {
            value: 10,
            message: "کد ملی باید ۱۰ رقم باشد",
          },
          maxLength: {
            value: 10,
            message: "کد ملی باید ۱۰ رقم باشد",
          },
        })}
        label="کد ملی"
        errorMessage={errors.nationalCode?.message}
      />

      <Submit loading={isSubmit} />
    </Form>
  );
}
